import React, { useEffect, useRef, useState } from "react";
import VariantTableDesktop from "./VariantTableDesktop";
import VariantTableMobile from "./VariantTableMobile";
import { translate } from "../../Services/translation";
import { VARIANT_TABLE_MOBILE_BREAKPOINT } from "../../constants";
import { useDispatch, useSelector } from "react-redux";
import { setVariantTableState } from "../../Actions/VariantTable.action";
import { Svg } from "../Svg";
import { Carousel as Lightbox } from "react-responsive-carousel";
import Modal from "react-modal";
import productImages from "../ProductImages";
import { setCurrentIndex, show } from "../../Actions/LightboxImages.action";

const lightboxSettings = {
    showStatus: false,
    showIndicators: false,
    useKeyboardArrows: true,
    thumbWidth: 50,
    autoPlay: false,
    autoFocus: true,
};

const customLightboxStyles = {
    content: {
        background: 'none',
        padding: 0,
        border: 'none',
    },
    overlay: {
        background: 'rgba(0, 0, 0, 0.88)',
        zIndex: 2003,
    },
};

/**
 * Create an on key/value object of the current url query params.
 *
 * @returns {{[p: string]: string}}
 */
export const getCurrentQueryParams = () => {
    const params = new URLSearchParams(window.location.search);
    const entries = Array.from(params.entries());
    return Object.fromEntries(entries);
};

/**
 *
 * @param {ProductItem} variants
 * @param {boolean} isAccessoryTable
 * @param scrollContainerRef
 * @param scrollContentRef
 * @param hideDefaultScroll
 * @param {boolean} ignoreDiscountedFilter
 * @param {boolean} isSelectedVariant
 * @returns {JSX.Element}
 * @constructor
 */
const VariantTable = ({ variants, isAccessoryTable = false, scrollContainerRef = null, scrollContentRef = null, hideDefaultScroll = false, ignoreDiscountedFilter = true, isSelectedVariant = false}) => {
    const dispatch = useDispatch();
    const { accessoryOpen, queryParams } = useSelector((state) => state.variantTable);
    let filterDiscounted = queryParams?.filterDiscounted === '1';

    let headers = [
        {
            label: '',
            prop: 'MainImageUrl',
            sortable: false,
            staticProp: true,
        },
        {
            label: translate('nmas.product.table.header.articledescription'),
            prop: 'id',
            propType: 'string',
            sortable: false,
            staticProp: true,
        }
    ];

    /**
     * Function to update the URL query parameters based on queryParams state
     *
     * @param newQueryParams
     */
    const setUrlQueryParams = (newQueryParams) => {
        const params = new URLSearchParams(newQueryParams).toString();
        let newUrl = `${window.location.pathname}`;
        let pathName = window.location.pathname + window.location.search;

        if (params !== '') {
            newUrl = `${window.location.pathname}?${params}`;
        }

        if (newUrl === pathName) return;

        window.history.pushState({}, '', newUrl);
    };

    let firstVariant = variants[0];
    if ('variantTableData' in firstVariant && firstVariant.variantTableData.length) {
        headers = headers.concat(firstVariant.variantTableData);
    }

    if (isAccessoryTable && accessoryOpen) {
        variants = variants.map(variant => {
            let hasQueryParams = variant.loginUrl.indexOf('?') !== -1;
            if (variant.loginUrl.indexOf('showAccessories') === -1) {
                let operator = hasQueryParams ? '&' : '?';
                variant.loginUrl += `${operator}showAccessories=${accessoryOpen}`;
            }

            return variant;
        });
    }

    const showPrices = !variants[0].isAnonymousUser && variants.filter(v => !v.hidePrice && v.price.hasPrice).length > 0;
    const enableSticky = true;
    const [styleProp, setStyleProp] = useState({});
    const [windowWidth, setWindowWidth] = useState(window.outerWidth); // Mainly to trigger a re-render.
    const [lightboxImages, setLightboxImages] = useState([]);

    const windowResizeRef = useRef(0);
    const tableRef = useRef(null);
    const colStockRef = useRef(null);
    const colPriceRef = useRef(null);
    const colQtyRef = useRef(null);
    const colAccessoriesRef = useRef(null);

    useEffect(() => {
        let colOffsetPrice = (colQtyRef.current ? colQtyRef.current.offsetWidth : 0) - 1;
        let colOffsetStock = colOffsetPrice + (colPriceRef.current ? colPriceRef.current.offsetWidth : 0) - 1;
        let colOffsetAccessories = colOffsetStock + (colStockRef.current ? colStockRef.current.offsetWidth : 0) - 1;

        setStyleProp({
            "--col-offset-stock": `${colOffsetStock}px`,
            "--col-offset-price": `${colOffsetPrice}px`,
            "--col-offset-accessories": `${colOffsetAccessories}px`
        });

    }, [windowWidth]);

    /**
     * Remove campaign filter from state, this will automatically update the url in the {@link setUrlQueryParams} and the useEffect below it.
     */
    const removeCampaignFilter = () => {
        const newParams = { ...queryParams };
        delete newParams['filterDiscounted'];

        dispatch(setVariantTableState({
            queryParams: newParams
        }))
    };

    const renderThumbnails = (children) => {
        return children.map((item, index) => {
            if (!item || !item.props || !item.props.children) {
                return;
            }

            // find img to get source
            const imageElement = item.props.children.find(
                (ele) => ele.type === 'img'
            );
            if (!imageElement || !imageElement.props || !imageElement.props.thumbnailSrcUrl) {
                return;
            }

            return (
                <div
                    key={index}
                    style={{
                        backgroundImage: 'url(' + imageElement.props.thumbnailSrcUrl + ')',
                    }}
                    className="thumbnail__image"
                />
            );
        });
    };

    /**
     * Used to show accessories modal on page load, if query parameter exists.
     */
    const handleShowAccessoriesParam = () => {
        const queryString = window.location.search;
        if (!queryString || !queryString.length) return;

        const urlParams = new URLSearchParams(queryString);
        if (!urlParams.size) return;

        const product = urlParams.get('showAccessories');
        if (!product) return;

        if (__litium.variants.filter(v => v.id === product && v.accessories.length > 0).length) {
            dispatch(setVariantTableState({ accessoryOpen: product }));
        }
    }

    const windowResizeHandler = () => {
        if (windowResizeRef.current) {
            clearTimeout(windowResizeRef.current);
        }

        windowResizeRef.current = setTimeout(() => {
            setWindowWidth(window.innerWidth);
        }, 100)
    }

    if (!ignoreDiscountedFilter) {
        useEffect(() => {
            setUrlQueryParams(queryParams);
        }, [queryParams]);
    }

    useEffect(() => {
        document.fonts.ready.then(() => setWindowWidth(windowWidth-1));
    }, []);

    useEffect(() => {
        if (!isAccessoryTable) {
            handleShowAccessoriesParam();
        }

        const handlePopState = () => {
            dispatch(setVariantTableState({ queryParams: getCurrentQueryParams() }));
        };

        window.addEventListener('popstate', handlePopState);
        window.addEventListener('resize', windowResizeHandler);

        return () => {
            window.removeEventListener('popstate', handlePopState);
            window.removeEventListener('resize', windowResizeHandler);
        }
    }, []);

    const getTableHeaders = (onlyDynamicProps = false) => {
        if (onlyDynamicProps) {
            return headers.filter(x => x.staticProp === false);
        }

        return headers;
    }

    let showQuantityCol = variants.filter(v => v.showBuyButton).length > 0;
    let showAccessoriesCol = false;
    if (variants.find(v => v.accessories !== null)) {
        showAccessoriesCol = variants.filter(v => v.accessories.length > 0).length > 0;
    }

    let tempVariants = [];
    if (!ignoreDiscountedFilter && filterDiscounted) {
        tempVariants = variants.filter(v => v.price?.campaignPrice !== null && v.price?.campaignPrice?.price > 0 && v.price?.campaignPrice?.price < v.price?.price?.price);

        if (tempVariants.length) {
            variants = tempVariants;
        }
    }

    let tableProps = {
        variants,
        tableRef,
        colStockRef,
        colPriceRef,
        colQtyRef,
        windowWidth,
        colAccessoriesRef,
        showQuantityCol,
        showPrices,
        showAccessoriesCol,
        getTableHeaders,
        isAccessoryTable,
        scrollContentRef,
        setLightboxImages
    }

    let tableInnerClassName = 'variant-table__table-scroll';
    tableInnerClassName += (!hideDefaultScroll ? ' variant-table__table-scroll--visible' : '');
    tableInnerClassName += (enableSticky ? ' variant-table--sticky-columns' : '');

    return (
        <>
            <div className={`variant-table ${isSelectedVariant ? 'variant-table__selected-variant' : ''}`}
                 style={styleProp} ref={tableRef}>
                {!isSelectedVariant && (!ignoreDiscountedFilter && filterDiscounted && tempVariants.length > 0) && (
                    <span className="variant-table__filter-item">
                    <strong>{translate('nmas.product.table.activefilters.label')}:&nbsp;</strong>
                    <span className="variant-table__filter-pill" onClick={() => removeCampaignFilter()}>
                        {translate('nmas.product.table.filter.campaign.label')}
                        <span className="variant-table__filter-pill-icon"><Svg icon="close" /></span>
                    </span>
                </span>
                )}

                <div className={tableInnerClassName} ref={scrollContainerRef}>
                    {windowWidth < VARIANT_TABLE_MOBILE_BREAKPOINT ?
                        <VariantTableMobile {...tableProps} /> :
                        <VariantTableDesktop {...tableProps} />}
                </div>
            </div>

            <Modal
                ariaHideApp={false}
                preventScroll={true}
                isOpen={lightboxImages.length > 0}
                onRequestClose={() => {
                    setLightboxImages([]);
                }}
                style={customLightboxStyles}
                shouldCloseOnOverlayClick={false}
            >
                <Lightbox
                    {...lightboxSettings}
                    selectedItem={0}
                    className="light-box"
                    renderThumbs={renderThumbnails}
                >
                    {lightboxImages.map((img, index) => (
                        <div
                            key={`figure${index}`}
                            className="light-box__container"
                        >
                            <button
                                onClick={() => {
                                    setLightboxImages([]);
                                }}
                                className="light-box__close-btn"
                            >
                                &times;
                            </button>
                            <img src={img.fullSrcUrl} thumbnailSrcUrl={img.thumbnailSrcUrl} className="light-box__image" />
                        </div>
                    ))}
                </Lightbox>
            </Modal>
        </>
    );
}


export default VariantTable;